import {
  type FC,
  useEffect,
  useMemo,
  useState,
} from 'react';

import AppLayout from '@/components/feature/AppLayout';

import {
  useAuth,
} from '@/contexts/AuthContext';

import {
  type ManagedUser,
  type UserRole,
  useUsers,
} from '@/hooks/useUsers';

const roleLabels: Record<
  UserRole,
  string
> = {
  admin:
    'Administrador',
  lawyer:
    'Advogado',
  secretary:
    'Secretaria',
};

const emptyForm = {
  name: '',
  email: '',
  role:
    'lawyer' as UserRole,
  password: '',
};

const UsersPage: FC = () => {
  const {
    user: currentUser,
  } = useAuth();

  const {
    users,
    loading,
    saving,
    loadUsers,
    createUser,
    updateUser,
  } = useUsers();

  const [
    error,
    setError,
  ] = useState('');

  const [
    success,
    setSuccess,
  ] = useState('');

  const [
    showForm,
    setShowForm,
  ] = useState(false);

  const [
    editing,
    setEditing,
  ] =
    useState<ManagedUser | null>(
      null
    );

  const [
    form,
    setForm,
  ] =
    useState(emptyForm);

  useEffect(() => {
    void loadUsers().catch(
      (loadError) => {
        setError(
          loadError instanceof
            Error
            ? loadError.message
            : 'Não foi possível carregar os usuários.'
        );
      }
    );
  }, [loadUsers]);

  const activeUsers =
    useMemo(
      () =>
        users.filter(
          (user) =>
            user.status ===
            'active'
        ).length,
      [users]
    );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const openEdit = (
    user: ManagedUser
  ) => {
    setEditing(user);

    setForm({
      name:
        user.name,
      email:
        user.email,
      role:
        user.role,
      password: '',
    });

    setError('');
    setSuccess('');
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) {
      return;
    }

    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
  };

  const submitForm =
    async () => {
      setError('');
      setSuccess('');

      if (
        !form.name.trim() ||
        !form.email.trim()
      ) {
        setError(
          'Nome e e-mail são obrigatórios.'
        );

        return;
      }

      if (
        !editing &&
        form.password.length < 10
      ) {
        setError(
          'A senha inicial deve ter ao menos 10 caracteres.'
        );

        return;
      }

      try {
        if (editing) {
          const payload: {
            name: string;
            email: string;
            role: UserRole;
            password?: string;
          } = {
            name:
              form.name.trim(),
            email:
              form.email.trim(),
            role:
              form.role,
          };

          if (
            form.password
          ) {
            payload.password =
              form.password;
          }

          await updateUser(
            editing.id,
            payload
          );

          setSuccess(
            'Usuário atualizado.'
          );
        } else {
          await createUser({
            name:
              form.name.trim(),
            email:
              form.email.trim(),
            role:
              form.role,
            password:
              form.password,
          });

          setSuccess(
            'Usuário criado.'
          );
        }

        setShowForm(false);
        setEditing(null);
        setForm(emptyForm);
      } catch (
        submitError
      ) {
        setError(
          submitError instanceof
            Error
            ? submitError.message
            : 'Não foi possível salvar o usuário.'
        );
      }
    };

  const toggleStatus =
    async (
      user: ManagedUser
    ) => {
      setError('');
      setSuccess('');

      try {
        await updateUser(
          user.id,
          {
            status:
              user.status ===
              'active'
                ? 'disabled'
                : 'active',
          }
        );

        setSuccess(
          user.status ===
            'active'
            ? 'Usuário desativado e sessões revogadas.'
            : 'Usuário ativado.'
        );
      } catch (
        toggleError
      ) {
        setError(
          toggleError instanceof
            Error
            ? toggleError.message
            : 'Não foi possível alterar o status.'
        );
      }
    };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">
              Usuários e acesso
            </h2>

            <p className="text-sm text-foreground-500 mt-0.5">
              Controle quem pode acessar o BM Legal e qual perfil cada pessoa possui.
            </p>
          </div>

          <button
            type="button"
            onClick={
              openCreate
            }
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-user-add-line mr-2" />
            Novo usuário
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">
              Total
            </p>
            <p className="text-2xl font-semibold text-foreground-900 mt-1">
              {
                users.length
              }
            </p>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">
              Ativos
            </p>
            <p className="text-2xl font-semibold text-foreground-900 mt-1">
              {
                activeUsers
              }
            </p>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-50 p-4">
            <p className="text-xs text-foreground-500">
              Seu perfil
            </p>
            <p className="text-sm font-semibold text-foreground-900 mt-2">
              {
                currentUser
                  ? roleLabels[
                      currentUser.role
                    ]
                  : '-'
              }
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="rounded-xl border border-background-200 bg-background-50 overflow-hidden">
          {loading ? (
            <div className="p-6 text-sm text-foreground-500">
              Carregando usuários...
            </div>
          ) : users.length ===
            0 ? (
            <div className="p-6 text-sm text-foreground-500">
              Nenhum usuário cadastrado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px]">
                <thead>
                  <tr className="border-b border-background-200 bg-background-100/60">
                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">
                      Usuário
                    </th>

                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">
                      Perfil
                    </th>

                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">
                      Status
                    </th>

                    <th className="text-left px-4 py-3 text-xs font-medium text-foreground-500">
                      Último acesso
                    </th>

                    <th className="text-right px-4 py-3 text-xs font-medium text-foreground-500">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {users.map(
                    (managedUser) => {
                      const isSelf =
                        managedUser.id ===
                        currentUser?.id;

                      return (
                        <tr
                          key={
                            managedUser.id
                          }
                          className="border-b last:border-b-0 border-background-100"
                        >
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-foreground-800">
                              {
                                managedUser.name
                              }
                              {isSelf && (
                                <span className="ml-2 text-[10px] text-primary-600">
                                  Você
                                </span>
                              )}
                            </p>

                            <p className="text-xs text-foreground-500">
                              {
                                managedUser.email
                              }
                            </p>
                          </td>

                          <td className="px-4 py-3 text-sm text-foreground-700">
                            {
                              roleLabels[
                                managedUser.role
                              ]
                            }
                          </td>

                          <td className="px-4 py-3">
                            <span
                              className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                                managedUser.status ===
                                'active'
                                  ? 'bg-green-50 text-green-700 border border-green-200'
                                  : 'bg-background-100 text-foreground-500 border border-background-200'
                              }`}
                            >
                              {managedUser.status ===
                              'active'
                                ? 'Ativo'
                                : 'Desativado'}
                            </span>
                          </td>

                          <td className="px-4 py-3 text-xs text-foreground-500">
                            {managedUser.lastLoginAt
                              ? new Date(
                                  managedUser.lastLoginAt
                                ).toLocaleString(
                                  'pt-BR'
                                )
                              : 'Nunca'}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  openEdit(
                                    managedUser
                                  )
                                }
                                className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-700 hover:bg-background-100 cursor-pointer"
                              >
                                Editar
                              </button>

                              {!isSelf && (
                                <button
                                  type="button"
                                  disabled={
                                    saving
                                  }
                                  onClick={() =>
                                    void toggleStatus(
                                      managedUser
                                    )
                                  }
                                  className="px-3 py-1.5 rounded-lg border border-background-200 text-xs text-foreground-700 hover:bg-background-100 disabled:opacity-50 cursor-pointer"
                                >
                                  {managedUser.status ===
                                  'active'
                                    ? 'Desativar'
                                    : 'Ativar'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4">
          <div className="w-full max-w-lg rounded-xl bg-background-50 border border-background-200 shadow-xl">
            <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-foreground-900">
                  {editing
                    ? 'Editar usuário'
                    : 'Novo usuário'}
                </h2>

                <p className="text-xs text-foreground-500 mt-1">
                  {editing
                    ? 'Alterações sensíveis revogam sessões existentes.'
                    : 'Crie um acesso individual para a equipe.'}
                </p>
              </div>

              <button
                type="button"
                onClick={
                  closeForm
                }
                className="w-8 h-8 rounded-lg hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1.5">
                  Nome
                </span>

                <input
                  value={
                    form.name
                  }
                  onChange={(
                    event
                  ) =>
                    setForm(
                      (
                        previous
                      ) => ({
                        ...previous,
                        name:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm focus:outline-none focus:border-primary-300"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1.5">
                  E-mail
                </span>

                <input
                  type="email"
                  value={
                    form.email
                  }
                  onChange={(
                    event
                  ) =>
                    setForm(
                      (
                        previous
                      ) => ({
                        ...previous,
                        email:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm focus:outline-none focus:border-primary-300"
                />
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1.5">
                  Perfil
                </span>

                <select
                  value={
                    form.role
                  }
                  disabled={
                    editing?.id ===
                    currentUser?.id
                  }
                  onChange={(
                    event
                  ) =>
                    setForm(
                      (
                        previous
                      ) => ({
                        ...previous,
                        role:
                          event
                            .target
                            .value as UserRole,
                      })
                    )
                  }
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm focus:outline-none focus:border-primary-300 disabled:opacity-60"
                >
                  <option value="admin">
                    Administrador
                  </option>
                  <option value="lawyer">
                    Advogado
                  </option>
                  <option value="secretary">
                    Secretaria
                  </option>
                </select>
              </label>

              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1.5">
                  {editing
                    ? 'Nova senha (opcional)'
                    : 'Senha inicial'}
                </span>

                <input
                  type="password"
                  value={
                    form.password
                  }
                  onChange={(
                    event
                  ) =>
                    setForm(
                      (
                        previous
                      ) => ({
                        ...previous,
                        password:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                  autoComplete="new-password"
                  placeholder={
                    editing
                      ? 'Deixe vazio para manter'
                      : 'Mínimo de 10 caracteres'
                  }
                  className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-sm focus:outline-none focus:border-primary-300"
                />
              </label>
            </div>

            <div className="px-5 py-4 border-t border-background-200 flex justify-end gap-2">
              <button
                type="button"
                disabled={
                  saving
                }
                onClick={
                  closeForm
                }
                className="px-4 py-2 rounded-lg border border-background-200 text-sm text-foreground-700 hover:bg-background-100 disabled:opacity-50 cursor-pointer"
              >
                Cancelar
              </button>

              <button
                type="button"
                disabled={
                  saving
                }
                onClick={() =>
                  void submitForm()
                }
                className="px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 cursor-pointer"
              >
                {saving
                  ? 'Salvando...'
                  : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default UsersPage;

